import { useEffect, useRef, useState, type FormEvent } from 'react';
import { contact } from '../content/site';
import SectionHeading from './SectionHeading';

type Status = 'idle' | 'sending' | 'success' | 'error';

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
    };
  }
}

export default function Contact() {
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const turnstileRef = useRef<HTMLDivElement>(null);
  const tokenRef = useRef<string>('');

  // Load Turnstile only if a site key is configured — keeps local dev friction-free.
  useEffect(() => {
    if (!contact.turnstileSiteKey || !turnstileRef.current) return;

    const scriptId = 'cf-turnstile-script';
    const render = () => {
      if (window.turnstile && turnstileRef.current) {
        window.turnstile.render(turnstileRef.current, {
          sitekey: contact.turnstileSiteKey,
          callback: (token: string) => {
            tokenRef.current = token;
          },
        });
      }
    };

    if (document.getElementById(scriptId)) {
      render();
      return;
    }
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.onload = render;
    document.body.appendChild(script);
  }, []);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus('sending');
    setErrorMsg('');

    const form = e.currentTarget;
    const data = new FormData(form);

    // Honeypot: bots tend to fill every field. Humans never see this one.
    if (data.get('company_website')) {
      setStatus('success'); // silently succeed for bots, no real send
      return;
    }

    const payload = {
      name: String(data.get('name') ?? ''),
      email: String(data.get('email') ?? ''),
      message: String(data.get('message') ?? ''),
      turnstileToken: tokenRef.current,
    };

    try {
      const res = await fetch(contact.submitEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? 'Something went wrong. Please try again.');
      }

      setStatus('success');
      form.reset();
      if (window.turnstile) window.turnstile.reset();
    } catch (err) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong.');
    }
  }

  return (
    <section id="contact" className="bg-cream py-24">
      <div className="container-page grid gap-12 md:grid-cols-2 md:items-start">
        <div>
          <SectionHeading eyebrow={contact.eyebrow} heading={contact.heading} />
          <p className="mt-4 max-w-md text-base leading-relaxed text-ink-soft">
            {contact.description}
          </p>
          <a
            href={`mailto:${contact.email}`}
            className="mt-4 inline-block text-sm font-semibold text-coral hover:underline"
          >
            {contact.email}
          </a>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {/* Honeypot field — hidden from sighted users, visible to bots */}
          <div className="hidden" aria-hidden="true">
            <label htmlFor="company_website">Leave this field empty</label>
            <input
              type="text"
              id="company_website"
              name="company_website"
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <div>
            <label htmlFor="name" className="mb-1 block text-sm font-medium text-ink">
              Name
            </label>
            <input
              id="name"
              name="name"
              type="text"
              required
              maxLength={120}
              className="w-full rounded-lg border border-ink/15 bg-cream px-4 py-2.5 text-ink outline-none transition-colors focus:border-coral"
            />
          </div>

          <div>
            <label htmlFor="email" className="mb-1 block text-sm font-medium text-ink">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              maxLength={200}
              className="w-full rounded-lg border border-ink/15 bg-cream px-4 py-2.5 text-ink outline-none transition-colors focus:border-coral"
            />
          </div>

          <div>
            <label htmlFor="message" className="mb-1 block text-sm font-medium text-ink">
              Message
            </label>
            <textarea
              id="message"
              name="message"
              required
              rows={4}
              maxLength={4000}
              className="w-full rounded-lg border border-ink/15 bg-cream px-4 py-2.5 text-ink outline-none transition-colors focus:border-coral"
            />
          </div>

          {contact.turnstileSiteKey && <div ref={turnstileRef} />}

          <button
            type="submit"
            disabled={status === 'sending'}
            className="w-full rounded-full bg-coral px-6 py-3 text-sm font-semibold text-cream shadow-soft transition-colors hover:bg-coral-dark disabled:cursor-not-allowed disabled:opacity-60"
          >
            {status === 'sending' ? 'Sending…' : 'Send message'}
          </button>

          <div role="status" aria-live="polite">
            {status === 'success' && (
              <p className="text-sm font-medium text-emerald-700">
                Thanks — your message is on its way. We'll be in touch soon.
              </p>
            )}
            {status === 'error' && <p className="text-sm font-medium text-red-700">{errorMsg}</p>}
          </div>
        </form>
      </div>
    </section>
  );
}
